import { useState, useEffect, useRef, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import axios from "axios";
import { scrollToTop } from "@/lib/utilsUI";
import AuthPromptModal from "@/components/Modals/AuthPromptModal";

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL;

function EmbajadorCard({ emb }) {
  const bg = emb.imgPerfil;

  return (
    <div className="min-w-[180px] max-w-[180px] h-[260px] rounded-2xl overflow-hidden relative flex-shrink-0 shadow-lg">
      {/* Fondo: imgPerfil ampliada y difuminada */}
      <div className="absolute inset-0">
        {bg ? (
          <img
            src={bg}
            alt=""
            className="w-full h-full object-cover scale-125 blur-xs"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-b from-aw-bluedark to-[#0d1b2a]" />
        )}
        {/* Overlay oscuro para contraste */}
        <div className="absolute inset-0 bg-black/55" />
      </div>

      {/* Contenido sobre el fondo */}
      <div className="relative z-10 flex flex-col items-center justify-center h-full px-3 py-4 gap-2">
        {bg ? (
          <img
            src={bg}
            alt={`${emb.nombre} ${emb.apellido}`}
            className="w-20 h-20 rounded-full object-cover ring-2 ring-aw-green shadow-lg"
            loading="lazy"
          />
        ) : (
          <div className="w-20 h-20 rounded-full bg-white/10 ring-2 ring-aw-green flex items-center justify-center text-2xl text-white/50 font-bold">
            {emb.nombre?.[0] || "?"}
          </div>
        )}

        <p className="text-white font-semibold text-sm text-center leading-tight mt-1">
          {emb.nombre} {emb.apellido}
        </p>

        {emb.alias && (
          <p className="text-aw-green text-xs text-center">@{emb.alias}</p>
        )}

        {emb.uidUsuario && (
          <Link
            to={`/usuario/${emb.alias || emb.uidUsuario}`}
            onClick={scrollToTop}
            className="mt-1 text-xs border border-aw-green text-aw-green px-3 py-1 rounded-full hover:bg-aw-green hover:text-black transition"
          >
            Ver perfil
          </Link>
        )}
      </div>
    </div>
  );
}

function SkeletonCard() {
  return (
    <div className="min-w-[180px] max-w-[180px] h-[260px] rounded-2xl overflow-hidden flex-shrink-0 bg-white/20 animate-pulse flex flex-col items-center justify-center gap-3 px-4">
      <div className="w-20 h-20 rounded-full bg-white/30" />
      <div className="w-24 h-3 rounded bg-white/30" />
      <div className="w-16 h-2 rounded bg-white/30" />
    </div>
  );
}

export default function EmbajadoresDestacados() {
  const [embajadores, setEmbajadores] = useState([]);
  const [page, setPage] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [authModalOpen, setAuthModalOpen] = useState(false);

  const { isAuthenticated, user } = useSelector((state) => state.auth);
  const userInfo = useSelector((state) => state.user.info);
  const navigate = useNavigate();

  const hasApplied = isAuthenticated && (
    user?.embajadorId != null ||
    userInfo?.ambassadorActive > 0
  );

  const handleEmbajadorClick = () => {
    if (isAuthenticated && user?.uid) {
      navigate(`/perfil/${user.uid}/activar-embajador`);
    } else {
      setAuthModalOpen(true);
    }
  };

  const scrollRef = useRef(null);
  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(false);

  useEffect(() => {
    const fetchInitial = async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${BACKEND_URL}/embajadores/public?page=1&limit=5`);
        setEmbajadores(data.embajadores || []);
        setHasNext(data.pagination?.hasNextPage || false);
        setPage(1);
      } catch (err) {
        console.error("Error cargando embajadores:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchInitial();
  }, []);

  const handleLoadMore = async () => {
    if (loadingMore || !hasNext) return;
    setLoadingMore(true);
    try {
      const nextPage = page + 1;
      const { data } = await axios.get(
        `${BACKEND_URL}/embajadores/public?page=${nextPage}&limit=5`
      );
      setEmbajadores((prev) => [...prev, ...(data.embajadores || [])]);
      setHasNext(data.pagination?.hasNextPage || false);
      setPage(nextPage);
    } catch (err) {
      console.error("Error cargando más embajadores:", err);
    } finally {
      setLoadingMore(false);
    }
  };

  const checkScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setShowLeft(el.scrollLeft > 0);
    setShowRight(el.scrollLeft + el.offsetWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener("scroll", checkScroll);
    checkScroll();
    return () => el.removeEventListener("scroll", checkScroll);
  }, [checkScroll, embajadores, loading]);

  const scroll = (dir) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ left: el.scrollLeft + (dir === "left" ? -500 : 500), behavior: "smooth" });
  };

  if (!loading && embajadores.length === 0) return null;

  return (
    <section className="my-8">
      {/* Encabezado */}
      <div className="mb-3 flex items-center justify-between gap-4">
        <div>
          <h3 className="text-xl font-semibold text-white">Staff de Embajadores</h3>
          <p className="text-sm text-gray-400 mt-0.5">Conoce a quienes hacen crecer la comunidad</p>
        </div>
        {!hasApplied && (
          <button
            onClick={handleEmbajadorClick}
            className="bg-aw-green text-aw-blueblack font-semibold px-4 py-2 rounded-3xl text-sm hover:bg-aw-green-dark transition duration-300 hover:scale-95 shrink-0"
          >
            <span className="hidden sm:inline">Postular como embajador</span>
            <span className="inline sm:hidden">Ser Embajador</span>
          </button>
        )}
      </div>

      {/* Carousel */}
      <div className="relative w-full">
        {/* Flecha izquierda */}
        {showLeft && (
          <button
            onClick={() => scroll("left")}
            className="absolute left-0 top-1/2 h-full -translate-y-1/2 z-10 bg-white/20 hover:bg-aw-bluedark-75 transition duration-300 p-2 rounded-tr-3xl rounded-br-3xl"
          >
            <FontAwesomeIcon icon="fa-solid fa-chevron-left" className="text-white text-lg" />
          </button>
        )}

        {/* Contenedor scroll */}
        <div
          ref={scrollRef}
          className="flex gap-4 overflow-x-auto scrollbar-hide scroll-smooth scrollbar-box pb-4"
          style={{ scrollBehavior: "smooth" }}
        >
          {loading
            ? Array.from({ length: 5 }).map((_, i) => <SkeletonCard key={i} />)
            : embajadores.map((emb) => <EmbajadorCard key={emb.id} emb={emb} />)}

          {/* Card "Ver más" al final del carousel */}
          {hasNext && !loading && (
            <div className="min-w-[180px] max-w-[180px] h-[260px] flex-shrink-0 flex items-center justify-center">
              <button
                onClick={handleLoadMore}
                disabled={loadingMore}
                className="w-full h-full flex flex-col items-center justify-center gap-3 bg-white/5 hover:bg-white/10 border-2 border-dashed border-aw-green hover:border-solid rounded-2xl transition-all duration-300 disabled:opacity-50"
              >
                {loadingMore ? (
                  <FontAwesomeIcon icon="fa-solid fa-spinner" className="animate-spin text-aw-green text-3xl" />
                ) : (
                  <>
                    <FontAwesomeIcon icon="fa-solid fa-plus-circle" className="text-aw-green text-3xl" />
                    <span className="text-white font-semibold text-sm">Ver más</span>
                  </>
                )}
              </button>
            </div>
          )}
        </div>

        {/* Flecha derecha */}
        {showRight && (
          <button
            onClick={() => scroll("right")}
            className="absolute right-0 top-1/2 h-full -translate-y-1/2 z-10 bg-white/20 hover:bg-aw-bluedark-75 transition duration-300 p-2 rounded-tl-3xl rounded-bl-3xl"
          >
            <FontAwesomeIcon icon="fa-solid fa-chevron-right" className="text-white text-lg" />
          </button>
        )}
      </div>

      <AuthPromptModal
        isOpen={authModalOpen}
        onClose={() => setAuthModalOpen(false)}
        returnTo="/embajador"
      />
    </section>
  );
}
